import { LOCALES, USER_ROLES } from '@plancia/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
// Relative, not `@/`: the API's integration spec loads this file at run time (console contract).
import { api } from '../../../lib/api/client';

// S25 · Users & access (07 §6, owner-only): users, API clients and the audit
// log, mirrored from apps/api/src/modules/users/dto.ts and
// apps/api/src/modules/audit/audit.controller.ts. Unknown keys pass through.

const nullableString = z.string().nullable();

export const userViewSchema = z.looseObject({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.enum(USER_ROLES),
  locale: z.enum(LOCALES).catch('it'),
  active: z.boolean(),
  totp_enabled: z.boolean(),
  last_login_at: nullableString,
});
export type UserView = z.infer<typeof userViewSchema>;

/** `GET /internal/users` */
export const userListSchema = z.looseObject({ items: z.array(userViewSchema) });
export type UserList = z.infer<typeof userListSchema>;

/** `POST /internal/users` without a password: the temporary one, shown once. */
export const createdUserSchema = userViewSchema.extend({ temporary_password: z.string().optional() });
export type CreatedUser = z.infer<typeof createdUserSchema>;

export const API_CLIENT_SCOPES = ['dokless', 'extension'] as const;

export const apiClientSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  /** `••••7c2e`: the key's last four (the key itself is shown once, at creation or rotation). */
  key_mask: z.string().optional(),
  scopes: z.array(z.string()),
  created_at: z.string(),
  last_used_at: nullableString,
  revoked_at: nullableString,
});
export type ApiClient = z.infer<typeof apiClientSchema>;

/** `GET /internal/api-clients` */
export const apiClientListSchema = z.looseObject({ items: z.array(apiClientSchema) });

/** `POST /internal/api-clients` and `…/:id/rotate`: the clear key, shown once. */
export const apiClientWithKeySchema = apiClientSchema.extend({ key: z.string() });
export type ApiClientWithKey = z.infer<typeof apiClientWithKeySchema>;

export const auditRowSchema = z.looseObject({
  id: z.string(),
  user_id: nullableString,
  action: z.string(),
  entity: z.string(),
  entity_id: nullableString,
  before: z.unknown(),
  after: z.unknown(),
  at: z.string(),
  ip: nullableString,
  /** Who acted, by name (null: Plancia itself). */
  actor_name: nullableString.optional(),
  /** What it is about in a person's words («MRSU4380667», «Mario Rossi»); null: no better name than the id. */
  entity_label: nullableString.optional(),
});
export type AuditRow = z.infer<typeof auditRowSchema>;

/** `GET /internal/audit` */
export const auditListSchema = z.looseObject({
  items: z.array(auditRowSchema),
  next_cursor: nullableString,
});
export type AuditList = z.infer<typeof auditListSchema>;

/** «Cosa»: the action families and the prefix the API filters by. */
export const AUDIT_FAMILIES = {
  user: 'user.',
  api_client: 'api_client.',
  setting: 'setting.',
  shipment: 'shipment.',
  carrier: 'carrier.',
  recipe: 'recipe',
  queue: 'queue.',
  delivery: 'delivery.',
  passage: 'passage.',
  location: 'location.',
  extension: 'extension_',
} as const;
export type AuditFamily = keyof typeof AUDIT_FAMILIES;
export const AUDIT_WHAT = Object.keys(AUDIT_FAMILIES) as [AuditFamily, ...AuditFamily[]];

export const AUDIT_PERIODS = ['1d', '7d', '30d', 'all'] as const;
export type AuditPeriod = (typeof AUDIT_PERIODS)[number];

/** The URL state of the tab (route `validateSearch`). */
export const usersSearchSchema = z.object({
  who: z.string().max(36).optional().catch(undefined),
  what: z.enum(AUDIT_WHAT).optional().catch(undefined),
  period: z.enum(AUDIT_PERIODS).optional().catch(undefined),
});
export type UsersSearch = z.infer<typeof usersSearchSchema>;

export interface AuditFilter {
  who: string | null;
  what: AuditFamily | null;
  period: AuditPeriod;
}

export const auditFilterOf = (search: UsersSearch): AuditFilter => ({
  who: search.who ?? null,
  what: search.what ?? null,
  period: search.period ?? '7d',
});

const PERIOD_MS: Record<Exclude<AuditPeriod, 'all'>, number> = {
  '1d': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
};

export function auditPath(filter: AuditFilter, now: number, before?: string | null): string {
  const q = new URLSearchParams();
  if (filter.who) q.set('user_id', filter.who);
  if (filter.what) q.set('action', AUDIT_FAMILIES[filter.what]);
  if (filter.period !== 'all') q.set('since', new Date(now - PERIOD_MS[filter.period]).toISOString());
  if (before) q.set('before', before);
  q.set('limit', '50');
  return `/internal/audit?${q.toString()}`;
}

export const usersKeys = {
  all: ['settings-users'] as const,
  users: ['settings-users', 'users'] as const,
  clients: ['settings-users', 'api-clients'] as const,
  audit: (filter: AuditFilter) => ['settings-users', 'audit', filter] as const,
};

export const usersQuery = queryOptions({
  queryKey: usersKeys.users,
  queryFn: ({ signal }) => api('/internal/users', { schema: userListSchema, signal }),
  retry: (count, error) => count < 2 && (error as { status?: number }).status !== 403,
});

export const apiClientsQuery = queryOptions({
  queryKey: usersKeys.clients,
  queryFn: ({ signal }) => api('/internal/api-clients', { schema: apiClientListSchema, signal }),
  retry: (count, error) => count < 2 && (error as { status?: number }).status !== 403,
});

export interface InviteInput {
  email: string;
  name: string;
  role: UserView['role'];
  locale: UserView['locale'];
}

export const usersApi = {
  /** No password: the API makes a temporary one and returns it once (no e-mail is ever sent). */
  invite: (input: InviteInput) =>
    api('/internal/users', { method: 'POST', body: input, schema: createdUserSchema }),
  update: (id: string, patch: Partial<Pick<UserView, 'name' | 'role' | 'locale' | 'active'>>) =>
    api(`/internal/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
      schema: userViewSchema,
    }),
  createClient: (name: string, scopes: string[]) =>
    api('/internal/api-clients', { method: 'POST', body: { name, scopes }, schema: apiClientWithKeySchema }),
  rotate: (id: string) =>
    api(`/internal/api-clients/${encodeURIComponent(id)}/rotate`, {
      method: 'POST',
      schema: apiClientWithKeySchema,
    }),
  revoke: (id: string, reason: string) =>
    api(`/internal/api-clients/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      body: { reason },
      schema: apiClientSchema,
    }),
  audit: (path: string, signal?: AbortSignal) =>
    api(path, { schema: auditListSchema, ...(signal ? { signal } : {}) }),
};
